import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';

export interface GrocyConfig {
  baseUrl: string;
  apiKey: string;
}

const STORAGE_KEY = 'grocy-config';
const CAMERA_DEVICE_KEY = 'camera-device-id';
const SHOW_COMPLETED_ITEMS_KEY = 'show-completed-items';
const QUERY_CACHE_KEY = 'GROCY_QUERY_CACHE';
// AI settings keys
const AI_PROVIDER_KEY = 'AI_PROVIDER';
const AI_OPENAI_API_KEY = 'AI_OPENAI_API_KEY';
const AI_OPENROUTER_API_KEY = 'AI_OPENROUTER_API_KEY';
const AI_MODEL_KEY = 'AI_MODEL';
const AI_FAST_MODEL_KEY = 'AI_FAST_MODEL';
const AI_VISION_MODEL_KEY = 'AI_VISION_MODEL';
const AI_TITLE_KEY = 'AI_TITLE';
const AI_SUGGESTIONS_KEY = 'AI_SUGGESTIONS_V1';
const AI_SUGGESTIONS_COLLAPSED_KEY = 'AI_SUGGESTIONS_COLLAPSED';
const AI_RECIPE_DETAIL_PREFIX = 'AI_RECIPE_DETAIL::';
const AI_EXCLUDED_LOCATIONS_KEY = 'AI_EXCLUDED_LOCATIONS';
const AI_EXCLUDED_PRODUCTS_KEY = 'AI_EXCLUDED_PRODUCTS';

// Create async storage persister for query cache
export const queryCachePersister = createAsyncStoragePersister({
  storage: typeof globalThis !== 'undefined' ? (globalThis as unknown as { localStorage?: Storage }).localStorage : undefined,
  key: QUERY_CACHE_KEY,
  throttleTime: 1000, // Throttle saves to avoid localStorage spamming
});

export const storage = {
  getConfig: (): GrocyConfig | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      
      const config = JSON.parse(stored);
      if (!config.baseUrl || !config.apiKey) {
        return null;
      }
      
      return config;
    } catch (error) {
      console.error('Error reading config from storage:', error);
      return null;
    }
  },

  setConfig: (config: GrocyConfig): void => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.error('Error saving config to storage:', error);
    }
  },

  clearConfig: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing config from storage:', error);
    }
  },

  hasConfig: (): boolean => {
    return storage.getConfig() !== null;
  }
};

export const getLastCameraDeviceId = (): string | null => {
  try {
    return localStorage.getItem(CAMERA_DEVICE_KEY);
  } catch (error) {
    console.error('Error reading camera device from storage:', error);
    return null;
  }
};

export const setLastCameraDeviceId = (deviceId: string): void => {
  try {
    localStorage.setItem(CAMERA_DEVICE_KEY, deviceId);
  } catch (error) {
    console.error('Error saving camera device to storage:', error);
  }
};

export const getShowCompletedItems = (): boolean => {
  try {
    const stored = localStorage.getItem(SHOW_COMPLETED_ITEMS_KEY);
    if (stored === null) {
      // Default to true if no preference is stored
      return true;
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading show completed items preference from storage:', error);
    // Default to true on error
    return true;
  }
};

export const setShowCompletedItems = (show: boolean): void => {
  try {
    localStorage.setItem(SHOW_COMPLETED_ITEMS_KEY, JSON.stringify(show));
  } catch (error) {
    console.error('Error saving show completed items preference to storage:', error);
  }
};

// Clear query cache data only (preserves login config)
export const clearQueryCache = (): void => {
  try {
    localStorage.removeItem(QUERY_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing query cache from storage:', error);
  }
}; 

// AI settings helpers
export type AIProvider = 'openai' | 'openrouter' | null;

export const getAIProvider = (): AIProvider => {
  try {
    const v = localStorage.getItem(AI_PROVIDER_KEY);
    return (v === 'openai' || v === 'openrouter') ? v : null;
  } catch (error) {
    console.error('Error reading AI provider from storage:', error);
    return null;
  }
};

export const setAIProvider = (provider: AIProvider): void => {
  try {
    if (!provider) {
      localStorage.removeItem(AI_PROVIDER_KEY);
    } else {
      localStorage.setItem(AI_PROVIDER_KEY, provider);
    }
  } catch (error) {
    console.error('Error saving AI provider to storage:', error);
  }
};

export const getOpenAIKey = (): string | null => {
  try {
    return localStorage.getItem(AI_OPENAI_API_KEY);
  } catch (error) {
    console.error('Error reading OpenAI key from storage:', error);
    return null;
  }
};

export const setOpenAIKey = (key: string | null): void => {
  try {
    if (key) localStorage.setItem(AI_OPENAI_API_KEY, key);
    else localStorage.removeItem(AI_OPENAI_API_KEY);
  } catch (error) {
    console.error('Error saving OpenAI key to storage:', error);
  }
};

export const getOpenRouterKey = (): string | null => {
  try {
    return localStorage.getItem(AI_OPENROUTER_API_KEY);
  } catch (error) {
    console.error('Error reading OpenRouter key from storage:', error);
    return null;
  }
};

export const setOpenRouterKey = (key: string | null): void => {
  try {
    if (key) localStorage.setItem(AI_OPENROUTER_API_KEY, key);
    else localStorage.removeItem(AI_OPENROUTER_API_KEY);
  } catch (error) {
    console.error('Error saving OpenRouter key to storage:', error);
  }
};

export const getAIModel = (): string | null => {
  try {
    return localStorage.getItem(AI_MODEL_KEY);
  } catch (error) {
    console.error('Error reading AI model from storage:', error);
    return null;
  }
};

export const setAIModel = (model: string | null): void => {
  try {
    if (model) localStorage.setItem(AI_MODEL_KEY, model);
    else localStorage.removeItem(AI_MODEL_KEY);
  } catch (error) {
    console.error('Error saving AI model to storage:', error);
  }
};

export const getAIFastModel = (): string | null => {
  try {
    return localStorage.getItem(AI_FAST_MODEL_KEY);
  } catch (error) {
    console.error('Error reading AI fast model from storage:', error);
    return null;
  }
};

export const setAIFastModel = (model: string | null): void => {
  try {
    if (model) localStorage.setItem(AI_FAST_MODEL_KEY, model);
    else localStorage.removeItem(AI_FAST_MODEL_KEY);
  } catch (error) {
    console.error('Error saving AI fast model to storage:', error);
  }
};

export const getAIVisionModel = (): string | null => {
  try {
    return localStorage.getItem(AI_VISION_MODEL_KEY);
  } catch (error) {
    console.error('Error reading AI vision model from storage:', error);
    return null;
  }
};

export const setAIVisionModel = (model: string | null): void => {
  try {
    if (model) localStorage.setItem(AI_VISION_MODEL_KEY, model);
    else localStorage.removeItem(AI_VISION_MODEL_KEY);
  } catch (error) {
    console.error('Error saving AI vision model to storage:', error);
  }
};

export const getAITitle = (): string | null => {
  try {
    return localStorage.getItem(AI_TITLE_KEY);
  } catch (error) {
    console.error('Error reading AI title from storage:', error);
    return null;
  }
};

export const setAITitle = (title: string | null): void => {
  try {
    if (title) localStorage.setItem(AI_TITLE_KEY, title);
    else localStorage.removeItem(AI_TITLE_KEY);
  } catch (error) {
    console.error('Error saving AI title to storage:', error);
  }
};

export const clearAIData = (): void => {
  try {
    localStorage.removeItem(AI_PROVIDER_KEY);
    localStorage.removeItem(AI_OPENAI_API_KEY);
    localStorage.removeItem(AI_OPENROUTER_API_KEY);
    localStorage.removeItem(AI_MODEL_KEY);
    localStorage.removeItem(AI_FAST_MODEL_KEY);
    localStorage.removeItem(AI_VISION_MODEL_KEY);
    localStorage.removeItem(AI_TITLE_KEY);
    localStorage.removeItem(AI_EXCLUDED_LOCATIONS_KEY);
    localStorage.removeItem(AI_EXCLUDED_PRODUCTS_KEY);
  } catch (error) {
    console.error('Error clearing AI settings from storage:', error);
  }
};

// AI location filtering settings
export const getAIExcludedLocations = (): Set<number> => {
  try {
    const stored = localStorage.getItem(AI_EXCLUDED_LOCATIONS_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored) as number[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (error) {
    console.error('Error reading AI excluded locations from storage:', error);
    return new Set();
  }
};

export const setAIExcludedLocations = (excludedLocationIds: Set<number>): void => {
  try {
    const array = Array.from(excludedLocationIds);
    localStorage.setItem(AI_EXCLUDED_LOCATIONS_KEY, JSON.stringify(array));
  } catch (error) {
    console.error('Error saving AI excluded locations to storage:', error);
  }
};

export const addAIExcludedLocation = (locationId: number): void => {
  const excluded = getAIExcludedLocations();
  excluded.add(locationId);
  setAIExcludedLocations(excluded);
};

export const removeAIExcludedLocation = (locationId: number): void => {
  const excluded = getAIExcludedLocations();
  excluded.delete(locationId);
  setAIExcludedLocations(excluded);
};

// AI product filtering settings
export const getAIExcludedProducts = (): Set<number> => {
  try {
    const stored = localStorage.getItem(AI_EXCLUDED_PRODUCTS_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored) as number[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (error) {
    console.error('Error reading AI excluded products from storage:', error);
    return new Set();
  }
};

export const setAIExcludedProducts = (excludedProductIds: Set<number>): void => {
  try {
    const array = Array.from(excludedProductIds);
    localStorage.setItem(AI_EXCLUDED_PRODUCTS_KEY, JSON.stringify(array));
  } catch (error) {
    console.error('Error saving AI excluded products to storage:', error);
  }
};

export const addAIExcludedProduct = (productId: number): void => {
  const excluded = getAIExcludedProducts();
  excluded.add(productId);
  setAIExcludedProducts(excluded);
};

export const removeAIExcludedProduct = (productId: number): void => {
  const excluded = getAIExcludedProducts();
  excluded.delete(productId);
  setAIExcludedProducts(excluded);
};

// AI dinner suggestions persistence
export interface StoredAISuggestionRecipe {
  title: string;
  description: string;
  estimated_minutes: number;
  servings: number;
  ingredients_description: string;
}

export interface StoredAISuggestions {
  recipes: StoredAISuggestionRecipe[];
  savedAt: number; // epoch ms
  reasoning?: string; // optional thinking trace
}

export const getAISuggestions = (): StoredAISuggestions | null => {
  try {
    const raw = localStorage.getItem(AI_SUGGESTIONS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAISuggestions;
    if (!parsed || !Array.isArray(parsed.recipes)) return null;
    return parsed;
  } catch (error) {
    console.error('Error reading AI suggestions from storage:', error);
    return null;
  }
};

export const setAISuggestions = (value: StoredAISuggestions | null): void => {
  try {
    if (!value) {
      localStorage.removeItem(AI_SUGGESTIONS_KEY);
      return;
    }
    localStorage.setItem(AI_SUGGESTIONS_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving AI suggestions to storage:', error);
  }
};

export const getAISuggestionsCollapsed = (): boolean => {
  try {
    const v = localStorage.getItem(AI_SUGGESTIONS_COLLAPSED_KEY);
    return v ? v === '1' : false;
  } catch (error) {
    console.error('Error reading collapsed state from storage:', error);
    return false;
  }
};

export const setAISuggestionsCollapsed = (collapsed: boolean): void => {
  try {
    localStorage.setItem(AI_SUGGESTIONS_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch (error) {
    console.error('Error saving collapsed state to storage:', error);
  }
};

// AI recipe detail persistence (keyed by suggestion content)
export interface StoredAIRecipeDetail {
  result: unknown;
  savedAt: number;
  reasoning?: string; // optional thinking trace
}

function hashString32(input: string): string {
  // Simple deterministic 32-bit hash for keys (not cryptographic)
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  // Convert to unsigned and hex
  return (hash >>> 0).toString(16);
}

export function computeRecipeDetailCacheKey(title: string, description: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80);
  const h = hashString32(`${title}|${description}`);
  return `${AI_RECIPE_DETAIL_PREFIX}${slug}::${h}`;
}

export function getAIRecipeDetail(cacheKey: string): StoredAIRecipeDetail | null {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAIRecipeDetail;
  } catch (error) {
    console.error('Error reading AI recipe detail from storage:', error);
    return null;
  }
}

export function setAIRecipeDetail(cacheKey: string, value: StoredAIRecipeDetail | null): void {
  try {
    if (!value) {
      localStorage.removeItem(cacheKey);
    } else {
      localStorage.setItem(cacheKey, JSON.stringify(value));
    }
  } catch (error) {
    console.error('Error saving AI recipe detail to storage:', error);
  }
}
